import axios from 'axios'
import { Auth } from 'aws-amplify'
import { newRequestKey } from './billing'

const base = 'https://content.selfbiography.com/api'
const wait = ms => new Promise(resolve => setTimeout(resolve, ms))
export async function videoSettings(value) {
  const headers = { Authorization: (await Auth.currentSession()).getAccessToken().getJwtToken() }
  const response = value ? await axios.put(`${base}/video-settings`, value, { headers }) : await axios.get(`${base}/video-settings`, { headers })

  return response.data
}
export async function uploadVideo(file, name, state, config) {
  const session = await Auth.currentSession()
  const owner = session.getAccessToken().payload.sub
  const fingerprint = `${owner}:${file.name || name}:${file.size}:${file.lastModified || 0}`
  const storageKey = `video-upload:${fingerprint}`
  try { state.videoId = state.videoId || localStorage.getItem(storageKey) } catch (_) { /* Storage is optional. */ }
  state.videoId = state.videoId || newRequestKey()
  try { localStorage.setItem(storageKey, state.videoId) } catch (_) { /* Storage is optional. */ }
  const id = state.videoId
  const request = async (action, data = {}, jobId = id) => axios.post(`${base}/video-uploads/${encodeURIComponent(jobId)}/${action}`, data, {
    timeout: 60000, headers: { Authorization: (await Auth.currentSession()).getAccessToken().getJwtToken() },
  })
  let cancelled = false
  let finished = false
  const cancel = async (jobId = id) => {
    await request('cancel', {}, jobId)
    cancelled = true
    try { localStorage.removeItem(storageKey) } catch (_) { /* Storage is optional. */ }
    state.videoId = null
    if (config.onCancelReady) config.onCancelReady(null)
  }
  if (config.onCancelReady) config.onCancelReady(() => cancel())
  try {
    let response = await request('start', { name: file.name || name, size: file.size, modified: file.lastModified || 0 })
    if (response.data.PartSize) {
      if (config.onProcessing) config.onProcessing(null)
      const size = response.data.PartSize
      const completed = new Set(response.data.Parts.map(p => p.PartNumber))
      let loaded = response.data.Parts.reduce((sum, p) => sum + p.Size, 0)
      if (config.onUploadProgress) config.onUploadProgress({ loaded, total: file.size })
      for (let number = 1; number <= Math.ceil(file.size / size); number += 1) {
        if (cancelled) throw new Error('Upload cancelled.')
        if (!completed.has(number)) {
          const part = file.slice((number - 1) * size, Math.min(file.size, number * size))
          const loadedBeforePart = loaded
          for (let attempt = 0; ; attempt += 1) {
            try {
              // Fresh URLs permit resuming long uploads without restarting completed parts.
              // eslint-disable-next-line no-await-in-loop
              const signed = await request('part', { number })
              // eslint-disable-next-line no-await-in-loop
              await axios.put(signed.data.url, part, {
                timeout: 300000,
                headers: { 'Content-Type': 'application/octet-stream' },
                onUploadProgress: event => { if (config.onUploadProgress) config.onUploadProgress({ loaded: loadedBeforePart + Math.min(event.loaded, part.size), total: file.size }) },
              })
              break
            } catch (error) {
              if (cancelled || attempt >= 2) throw error
              // eslint-disable-next-line no-await-in-loop
              await wait(1000 * (attempt + 1))
            }
          }
          loaded += part.size
        }
      }
      if (cancelled) throw new Error('Upload cancelled.')
      if (config.onProcessing) config.onProcessing('validating')
      response = await request('complete')
    } else if (response.data.Phase === 'validating') response = await request('complete')
    const deadline = Date.now() + 60 * 60 * 1000
    while (response.data.Processing) {
      if (cancelled) throw new Error('Upload cancelled.')
      if (config.onProcessing) config.onProcessing(response.data.Phase)
      if (Date.now() > deadline) throw new Error('Your video is still processing. Retry to check its progress.')
      // eslint-disable-next-line no-await-in-loop
      await wait(4000)
      // eslint-disable-next-line no-await-in-loop
      response = await request('status')
    }
    if (!response.data.Data || !response.data.Data.PosterKey) throw new Error('The video preview could not be confirmed.')
    finished = true
    try { localStorage.removeItem(storageKey) } catch (_) { /* Storage is optional. */ }

    return response
  } catch (error) {
    if (error.response && error.response.data.error === 'VIDEO_BUSY' && error.response.data.JobId && config.onCancelReady) {
      config.onCancelReady(() => cancel(error.response.data.JobId))
    }
    if (error.response && ['UPLOAD_EXPIRED', 'VIDEO_PROCESSING_FAILED'].includes(error.response.data.error)) {
      try { localStorage.removeItem(storageKey) } catch (_) { /* Storage is optional. */ }
      state.videoId = null
    }
    throw error
  } finally { if ((finished || cancelled) && config.onCancelReady) config.onCancelReady(null) }
}
